import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { NotificationService } from '../notification/notification.service';
import { EtagStore } from './etag-store.service';

const ETAG_MISMATCH_TOAST =
  'This case was updated by someone else. Please refresh and try again.';

/**
 * Detects 409 ETAG_MISMATCH responses raised by the pipeline's
 * `etag_required` dependency, surfaces a refresh toast, and wipes the stale
 * entry from `EtagStore` so the next read repopulates it.
 *
 * The pipeline error envelope shape (matching `etag_required`):
 *
 *   {
 *     "detail": {
 *       "error": {
 *         "code": "ETAG_MISMATCH",
 *         "message": "Document has changed since you last read it.",
 *         "details": { "currentEtag": "\"v2\"" }
 *       }
 *     }
 *   }
 *
 * FastAPI wraps `HTTPException(detail=...)` so the body lives at `error.detail.error`
 * — but some error wrappers strip the `detail` layer, so we check both paths.
 */
@Injectable()
export class EtagConflictInterceptor implements HttpInterceptor {
  static readonly TOAST_TEXT = ETAG_MISMATCH_TOAST;

  constructor(
    private readonly notify: NotificationService,
    private readonly etags: EtagStore,
  ) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 409 && this.isEtagMismatch(err)) {
          this.handleConflict(req, err);
        }
        return throwError(() => err);
      }),
    );
  }

  private isEtagMismatch(err: HttpErrorResponse): boolean {
    return getErrorCode(err) === 'ETAG_MISMATCH';
  }

  private handleConflict(req: HttpRequest<unknown>, err: HttpErrorResponse): void {
    this.notify.error(ETAG_MISMATCH_TOAST);
    const caseId = this.extractCaseId(req);
    if (caseId) this.etags.clear(caseId);

    // If the server told us the current etag, re-stash it so the next mutation
    // (after the operator refreshes) doesn't have to wait on a fresh GET.
    const fresh = getCurrentEtag(err);
    if (fresh && caseId) this.etags.set(caseId, fresh);
  }

  private extractCaseId(req: HttpRequest<unknown>): string | null {
    const riskMatch = /\/risk\/fact-rules\/([^/?]+)/.exec(req.url);
    if (riskMatch) return decodeURIComponent(riskMatch[1]);
    const caseMatch = /\/cases\/([^/?]+)/.exec(req.url);
    if (caseMatch && caseMatch[1] !== 'detail') return decodeURIComponent(caseMatch[1]);
    return null;
  }
}

function getErrorCode(err: HttpErrorResponse): string | null {
  const body = err.error as
    | { error?: { code?: string }; detail?: { error?: { code?: string } } }
    | null
    | undefined;
  if (!body) return null;
  if (body.error?.code) return body.error.code;
  if (body.detail?.error?.code) return body.detail.error.code;
  return null;
}

function getCurrentEtag(err: HttpErrorResponse): string | null {
  const body = err.error as
    | {
        error?: { details?: { currentEtag?: string } };
        detail?: { error?: { details?: { currentEtag?: string } } };
      }
    | null
    | undefined;
  if (!body) return null;
  return (
    body.error?.details?.currentEtag ?? body.detail?.error?.details?.currentEtag ?? null
  );
}
